// DTO ⇄ view model for the Monitor ▸ Inventory list and its detail header.
import { formatTextOrDash } from '@/lib/format/text';
import { formatQuantityOrDash } from '@/lib/format/number';
import { buildQuantitiesByUnit, defaultUnitType } from '@/lib/mappers/quantities';
import { unitSymbol } from '@/lib/mappers/units';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { materialRoleLabel } from '@/config/statuses';
import { buildInventoryKey } from '../constants/inventoryKey';

/** Screen state → `inventory` variables. Site and terminal come from the session, not from here. */
export const buildInventoryQuery = ({ page, pageSize, search } = {}) => ({
  page,
  limit: pageSize,
  // `InventorySearch` ANDs its fields, so the one box drives the material name alone.
  search: search?.trim()
    ? { materialName: { contains: search.trim(), mode: 'insensitive' } }
    : undefined,
});

// Only one of the three facility ids is set; `facilityType` says which.
const facilityIdOf = (node) => node.siteId ?? node.storageId ?? node.backToBackAccountId;
const facilityNameOf = (node) => node.siteName ?? node.storageName ?? node.backToBackAccountName;

const buildByUnit = (node) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.displayUnit,
      amounts: { availableQuantity: node.totalCertifiedQuantityWorking },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.weightUnit,
      amounts: { availableQuantity: node.totalCertifiedQuantityDeclaration },
    },
  });

export const processInventoryRow = (node, index, page) => {
  const byUnit = buildByUnit(node);
  const facilityId = facilityIdOf(node);
  // `node.id` is the MATERIAL's id, repeated across its facility/terminal rows.
  const id = buildInventoryKey({
    facilityType: node.facilityType,
    facilityId,
    materialId: node.id,
    terminalId: node.terminalId,
  });
  return {
    id,
    // 1-indexed across the whole list, so page 2 does not restart at 1.
    serialNo: String(page.offset + index + 1),
    materialName: formatTextOrDash(node.materialName),
    materialId: formatTextOrDash(node.materialId),
    materialRole: formatTextOrDash(materialRoleLabel(node.materialRole)),
    facilityName: formatTextOrDash(facilityNameOf(node)),
    terminal: formatTextOrDash(node.terminal),
    byUnit,
    unitType: defaultUnitType(byUnit),
  };
};

export const processInventoryPage = (connection) => {
  const pageInfo = connection?.pageInfo;
  const limit = pageInfo?.limit ?? 0;
  const offset = ((pageInfo?.page ?? 1) - 1) * limit;
  return {
    rows: (connection?.nodes ?? []).map((node, index) =>
      processInventoryRow(node, index, { offset }),
    ),
    totalCount: pageInfo?.total ?? 0,
  };
};

// TODO: `inventoryItem` exposes no unit fields, unlike the list node — the header shows bare amounts.
export const processInventoryItem = (node) => {
  if (!node) return null;

  return {
    materialId: formatTextOrDash(node.materialId),
    materialName: formatTextOrDash(node.materialName),
    facilityName: formatTextOrDash(facilityNameOf(node)),
    terminal: formatTextOrDash(node.terminal),
    availableQuantityDeclaration: formatQuantityOrDash(node.totalCertifiedQuantityDeclaration),
    availableQuantityWorking: formatQuantityOrDash(node.totalCertifiedQuantityWorking),
    availableQuantityTracking: formatQuantityOrDash(node.totalCertifiedQuantityTracking),
    // The server explains a missing tracking figure; the header renders the message, not a 0.
    trackingUnitStatus: node.trackingUnitStatus ?? null,
    trackingUnitMessage: node.trackingUnitMessage ?? null,
    // `weightUnit` is the declaration unit and `displayUnit` the working one (see CLAUDE.md).
    declarationUnit: unitSymbol(node.weightUnit),
    workingUnit: unitSymbol(node.displayUnit),
  };
};
